package br.com.texeratx.boardgametimeapp.ui.screens.myclans

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.width
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.ArrowBack
import androidx.compose.material.icons.filled.CameraAlt
import androidx.compose.material3.Button
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedButton
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.Text
import androidx.compose.material3.TopAppBar
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import br.com.texeratx.boardgametimeapp.core.image.ImageSourcePickerDialog
import br.com.texeratx.boardgametimeapp.core.image.LocalImagePreview
import br.com.texeratx.boardgametimeapp.core.image.PickedImage
import br.com.texeratx.boardgametimeapp.core.image.rememberImageSourceState
import br.com.texeratx.boardgametimeapp.core.session.SessionManager
import br.com.texeratx.boardgametimeapp.data.repository.ClanRepository
import br.com.texeratx.boardgametimeapp.domain.usecases.upload.UploadUserImageUseCase
import br.com.texeratx.boardgametimeapp.ui.components.PlayerAvatar
import br.com.texeratx.boardgametimeapp.ui.components.designsystem.AppLoadingOverlay
import br.com.texeratx.boardgametimeapp.ui.components.designsystem.AppScreenScaffold
import br.com.texeratx.boardgametimeapp.ui.components.designsystem.AppTopBarColors
import cafe.adriel.voyager.core.model.ScreenModel
import cafe.adriel.voyager.core.model.screenModelScope
import cafe.adriel.voyager.core.screen.Screen
import cafe.adriel.voyager.koin.getScreenModel
import cafe.adriel.voyager.navigator.LocalNavigator
import cafe.adriel.voyager.navigator.currentOrThrow
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.launch

class EditProfileUiState(
    val displayName: String = "",
    val currentImageUrl: String? = null,
    val localImageBytes: ByteArray? = null,
    val localImageMimeType: String? = null,
    val isUploadingImage: Boolean = false,
    val isSaving: Boolean = false,
    val error: String? = null,
) {
    fun copy(
        displayName: String = this.displayName,
        currentImageUrl: String? = this.currentImageUrl,
        localImageBytes: ByteArray? = this.localImageBytes,
        localImageMimeType: String? = this.localImageMimeType,
        isUploadingImage: Boolean = this.isUploadingImage,
        isSaving: Boolean = this.isSaving,
        error: String? = this.error,
    ) = EditProfileUiState(displayName, currentImageUrl, localImageBytes, localImageMimeType, isUploadingImage, isSaving, error)
}

class EditProfileViewModel(
    private val repo: ClanRepository,
    private val sessionManager: SessionManager,
    private val uploadUserImageUseCase: UploadUserImageUseCase,
) : ScreenModel {

    private val _state = MutableStateFlow(EditProfileUiState())
    val state = _state.asStateFlow()

    private var pendingImage: PickedImage? = null

    fun load() {
        screenModelScope.launch {
            val session = sessionManager.load() ?: return@launch
            _state.value = _state.value.copy(
                displayName = session.displayName,
                currentImageUrl = session.userImageUrl
            )
        }
    }

    fun onDisplayNameChange(value: String) {
        _state.value = _state.value.copy(displayName = value, error = null)
    }

    fun onImageSelected(image: PickedImage) {
        pendingImage = image
        _state.value = _state.value.copy(
            localImageBytes = image.bytes,
            localImageMimeType = image.mimeType,
            error = null
        )
    }

    fun clearLocalImage() {
        pendingImage = null
        _state.value = _state.value.copy(
            localImageBytes = null,
            localImageMimeType = null
        )
    }

    fun save(onSuccess: () -> Unit) {
        val name = _state.value.displayName.trim()
        if (name.isBlank()) {
            _state.value = _state.value.copy(error = "O nome não pode ficar em branco")
            return
        }
        screenModelScope.launch {
            _state.value = _state.value.copy(isSaving = true, error = null)

            var finalImageUrl: String? = _state.value.currentImageUrl
            val pending = pendingImage

            if (pending != null) {
                _state.value = _state.value.copy(isUploadingImage = true)
                uploadUserImageUseCase(pending.bytes, pending.fileName, pending.mimeType).fold(
                    onSuccess = { url ->
                        finalImageUrl = url
                        _state.value = _state.value.copy(isUploadingImage = false)
                    },
                    onFailure = { e ->
                        _state.value = _state.value.copy(
                            isUploadingImage = false,
                            isSaving = false,
                            error = e.message ?: "Erro ao enviar imagem"
                        )
                        return@launch
                    }
                )
            }

            repo.updateMyUser(displayName = name, imageUrl = finalImageUrl).fold(
                onSuccess = { dto ->
                    val session = sessionManager.load() ?: return@fold
                    sessionManager.save(
                        session.copy(
                            displayName = dto.displayName,
                            userImageUrl = dto.imageUrl
                        )
                    )
                    pendingImage = null
                    _state.value = _state.value.copy(
                        isSaving = false,
                        displayName = dto.displayName,
                        currentImageUrl = dto.imageUrl,
                        localImageBytes = null,
                        localImageMimeType = null
                    )
                    onSuccess()
                },
                onFailure = { e ->
                    _state.value = _state.value.copy(
                        isSaving = false,
                        error = e.message ?: "Erro ao salvar perfil"
                    )
                }
            )
        }
    }
}

class EditProfileScreen : Screen {

    @OptIn(ExperimentalMaterial3Api::class)
    @Composable
    override fun Content() {
        val vm = getScreenModel<EditProfileViewModel>()
        val state by vm.state.collectAsState()
        val navigator = LocalNavigator.currentOrThrow

        LaunchedEffect(Unit) { vm.load() }

        val imageSource = rememberImageSourceState { image ->
            image?.let { vm.onImageSelected(it) }
        }
        ImageSourcePickerDialog(imageSource)

        AppScreenScaffold(
            topBar = {
                TopAppBar(
                    title = { Text("Editar perfil", fontWeight = FontWeight.Bold) },
                    navigationIcon = {
                        IconButton(onClick = { navigator.pop() }) {
                            Icon(Icons.Default.ArrowBack, contentDescription = "Voltar")
                        }
                    },
                    colors = AppTopBarColors()
                )
            }
        ) { padding ->
            Box(
                Modifier
                    .padding(padding)
                    .fillMaxSize()
            ) {
                Column(
                    Modifier
                        .fillMaxSize()
                        .padding(16.dp),
                    verticalArrangement = Arrangement.spacedBy(16.dp),
                    horizontalAlignment = Alignment.CenterHorizontally
                ) {
                    // ── Avatar ────────────────────────────────────────────
                    Box(contentAlignment = Alignment.BottomEnd) {
                        if (state.localImageBytes != null) {
                            LocalImagePreview(
                                bytes = state.localImageBytes!!,
                                modifier = Modifier.size(96.dp)
                            )
                        } else {
                            PlayerAvatar(
                                imageUrl = state.currentImageUrl,
                                contentDescription = state.displayName,
                                size = 96.dp
                            )
                        }
                        IconButton(
                            onClick = { imageSource.onLaunch() },
                            modifier = Modifier.size(32.dp)
                        ) {
                            Icon(
                                Icons.Default.CameraAlt,
                                contentDescription = "Alterar foto",
                                tint = MaterialTheme.colorScheme.primary,
                                modifier = Modifier.size(20.dp)
                            )
                        }
                    }

                    if (state.localImageBytes != null) {
                        OutlinedButton(
                            onClick = { vm.clearLocalImage() },
                            modifier = Modifier.fillMaxWidth()
                        ) { Text("Remover foto selecionada") }
                    }

                    // ── Nome ──────────────────────────────────────────────
                    OutlinedTextField(
                        value = state.displayName,
                        onValueChange = vm::onDisplayNameChange,
                        label = { Text("Nome de exibição") },
                        singleLine = true,
                        modifier = Modifier.fillMaxWidth()
                    )

                    state.error?.let {
                        Text(it, color = MaterialTheme.colorScheme.error)
                    }

                    // ── Salvar ────────────────────────────────────────────
                    Button(
                        onClick = { vm.save { navigator.pop() } },
                        enabled = !state.isSaving && state.displayName.isNotBlank(),
                        modifier = Modifier.fillMaxWidth()
                    ) {
                        if (state.isUploadingImage) {
                            CircularProgressIndicator(
                                color = MaterialTheme.colorScheme.onPrimary,
                                modifier = Modifier.size(18.dp)
                            )
                            Spacer(Modifier.width(8.dp))
                            Text("Enviando imagem...")
                        } else if (state.isSaving) {
                            CircularProgressIndicator(
                                color = MaterialTheme.colorScheme.onPrimary,
                                modifier = Modifier.size(18.dp)
                            )
                            Spacer(Modifier.width(8.dp))
                            Text("Salvando...")
                        } else {
                            Text("Salvar")
                        }
                    }
                }

                AppLoadingOverlay(isVisible = state.isSaving)
            }
        }
    }
}
